/**
 * The third door.
 *
 * A claim's rationale and its proposed wording had exactly one way in: an empty
 * textarea and the word "Draft it". A rep who has never written an industrial
 * argument looks at that and writes nothing, which is why most of the claims in
 * this workbench carry a title and no case. The ask was for a generate button,
 * and the reason it has to be three doors rather than one is in the ask itself:
 * "generate then edit, or type in their own, or type in their own and then
 * generate with their own as the seed." The third is the one that gets used —
 * a rep usually has the argument and is stuck on the register, and a model
 * given their sentence returns their case in longer form rather than its own
 * case in place of theirs.
 *
 * What this component is actually for is the line after the generation.
 *
 * **Generated text never quietly becomes a person's words.** It lands in the
 * editor, unsaved, under a stamp that names the model, and the claim does not
 * change until someone presses Save. That is the whole provenance mechanism and
 * it is deliberately not automatic: `drafted_by` and `plain_state` exist all
 * through this schema because the group's rule is that nothing AI-produced may
 * look like a fact from the document or like a person's own words, and a
 * rationale is the softest place in the product to break that — it is prose, in
 * the group's voice, exported as the case for the claim.
 *
 * **The stamp changes when the person does.** Untouched machine text wears
 * `.stamp-ai` and the ◔ that means unchecked everywhere else here. Once a
 * character differs it becomes "edited by you, from a draft by X" — still true,
 * no longer a warning, because a person has now read every word of it.
 *
 * **The assumptions are the point.** A fabricated sentence in a rationale is
 * indistinguishable from a sourced one by reading — "teachers here routinely
 * work beyond the timetabled hours" is fluent, plausible, and something nobody
 * measured. The route asks the model to enumerate what it treated as true
 * without being told, and that list is shown here as a checklist, because a
 * risk you can tick off is a different thing from a risk you have to notice.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { draftStamp, type MachineDraft } from './provenance.js';
import { api } from '../lib/api.js';

export function DraftWithAI({
  claimId,
  field,
  value,
  onDrafted,
  machine,
}: {
  claimId: string;
  field: 'rationale' | 'wording';
  /** What is in the editor now. Empty means generate from nothing; anything else is a seed. */
  value: string;
  onDrafted: (drafted: MachineDraft) => void;
  machine: MachineDraft | null;
}) {
  const [dismissed, setDismissed] = useState(false);
  const seed = value.trim();

  const generate = useMutation<MachineDraft, Error>({
    mutationFn: () => api.draftClaimText(claimId, { field, ...(seed ? { seed } : {}) }),
    onSuccess: (drafted) => {
      setDismissed(false);
      onDrafted(drafted);
    },
  });

  const stamp = draftStamp(value, machine);

  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => generate.mutate()}
          disabled={generate.isPending}
          className="hit label underline"
        >
          {generate.isPending
            ? 'Drafting…'
            : /*
                 The same button, named for what it will actually do. Pressed
                 with words in the box it develops them; pressed empty it starts
                 from nothing. Two buttons would make a rep choose between them
                 before knowing the difference.
               */
              seed
              ? 'Develop what I have written'
              : 'Generate with AI'}
        </button>
        {generate.error ? (
          <span className="text-[11px]" style={{ color: 'var(--color-defect)' }}>
            {generate.error.message}
          </span>
        ) : null}
      </div>

      {machine && stamp ? (
        <div className="mt-2">
          <span className={stamp.unchecked ? 'stamp stamp-ai' : 'stamp'}>{stamp.text}</span>

          {machine.assumptions.length > 0 && !dismissed ? (
            <div
              className="measure mt-2 border-l-2 pl-3"
              style={{ borderColor: 'var(--color-rule-strong)' }}
            >
              <p className="label m-0">
                What this draft assumes — {machine.assumptions.length}, none of them from the
                agreement
              </p>
              <ul className="mt-1">
                {machine.assumptions.map((assumption) => (
                  <li key={assumption} className="text-[12px] leading-snug">
                    · {assumption}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => setDismissed(true)}
                className="hit label mt-1 underline"
              >
                I have dealt with these
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
